import type { ReactNode } from 'react'
import { CheckCircle, Users, Phone, XCircle, TrendingUp } from 'lucide-react'
import type { CloserLeadSummary } from '@/types'
import { CloserMetricsBreakdown } from '@/components/CloserLeads/CloserMetricsBreakdown'

interface MetricCardProps {
  label: string
  value: number | string
  icon: React.ReactNode
  accent: string
}

function MetricCard({ label, value, icon, accent }: MetricCardProps) {
  return (
    <div className='glass-card flex items-center gap-3 p-3 sm:gap-4 sm:p-4'>
      <div
        className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl'
        style={{ background: `${accent}15`, color: accent }}
      >
        {icon}
      </div>
      <div className='min-w-0'>
        <p
          className='font-medium uppercase tracking-wider text-text-muted'
          style={{ fontSize: 'var(--font-size-label)' }}
        >
          {label}
        </p>
        <p className='text-xl font-black tracking-tight text-text-primary'>{value}</p>
      </div>
    </div>
  )
}

function MetricSkeleton() {
  return (
    <div className='glass-card flex animate-pulse items-center gap-4 p-4'>
      <div className='h-10 w-10 shrink-0 rounded-xl bg-border-subtle' />
      <div className='min-w-0 flex-1 space-y-2'>
        <div className='h-2 w-16 rounded bg-border-subtle' />
        <div className='h-5 w-10 rounded bg-border-subtle' />
      </div>
    </div>
  )
}

const PERIOD_OPTIONS = [
  { value: 'month', label: 'Mês' },
  { value: 'quarter', label: 'Trimestre' },
  { value: 'year', label: 'Ano' },
] as const

interface CloserMetricsProps {
  summary: CloserLeadSummary
  loading?: boolean
  period: string
  onPeriodChange: (period: string) => void
  isAdminOrManager?: boolean
  funnel?: ReactNode
  expanded: boolean
}

const METRICS_GRID = 'grid grid-cols-2 gap-2.5 sm:grid-cols-3'

export function CloserMetrics({
  summary,
  loading,
  period,
  onPeriodChange,
  isAdminOrManager,
  funnel,
  expanded,
}: CloserMetricsProps) {
  if (!expanded) return null

  const conversionRate =
    summary.conversionRate ??
    (summary.total > 0 ? Math.round((summary.convertidos / summary.total) * 100) : 0)

  return (
    <section className='space-y-4' aria-label='Resumo de leads'>
      <div className='flex flex-wrap items-center justify-end gap-2'>
            <div
              className='inline-flex items-center gap-1 rounded-xl border border-border-subtle bg-[var(--glass-bg)] p-1'
              role='group'
              aria-label='Período'
            >
              {PERIOD_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type='button'
                  onClick={() => onPeriodChange(opt.value)}
                  className={`min-h-[36px] rounded-lg px-4 py-1.5 text-xs font-semibold transition-all ${
                    period === opt.value
                      ? 'bg-accent text-white shadow-sm'
                      : 'text-text-muted hover:bg-border-subtle hover:text-text-primary'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className='grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_260px] lg:items-stretch'>
              <div className={METRICS_GRID}>
                {Array.from({ length: 6 }).map((_, i) => (
                  <MetricSkeleton key={i} />
                ))}
              </div>
              <div className='glass-card min-h-[200px] animate-pulse rounded-2xl bg-border-subtle lg:min-h-0' />
            </div>
          ) : (
            <>
              <div className='grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_260px] lg:items-stretch'>
                <div className={METRICS_GRID}>
                  <MetricCard label='Total' value={summary.total} icon={<Users size={18} />} accent='#14bdbd' />
                  <MetricCard label='Novos' value={summary.novos} icon={<Users size={18} />} accent='#3b82f6' />
                  <MetricCard
                    label='Em Contato'
                    value={summary.emContato}
                    icon={<Phone size={18} />}
                    accent='#f59e0b'
                  />
                  <MetricCard
                    label='Convertidos'
                    value={summary.convertidos}
                    icon={<CheckCircle size={18} />}
                    accent='#22c55e'
                  />
                  <MetricCard
                    label='Descartados'
                    value={summary.descartados}
                    icon={<XCircle size={18} />}
                    accent='#ef4444'
                  />
                  <MetricCard
                    label='Conversão'
                    value={`${conversionRate}%`}
                    icon={<TrendingUp size={18} />}
                    accent='#a855f7'
                  />
                </div>
                {funnel ? <div className='min-w-0 lg:h-full'>{funnel}</div> : null}
              </div>

              <CloserMetricsBreakdown summary={summary} isAdminOrManager={isAdminOrManager} />
            </>
          )}
    </section>
  )
}

export default CloserMetrics
